/**
 * Sound Service - Cross-platform audio for PokerSweepStars
 * 
 * Uses Capacitor NativeAudio for iOS/Android apps (reliable, no restrictions)
 * Falls back to HTML5 Audio for web browser
 * 
 * SETUP (for native apps):
 * 1. npm install @capacitor-community/native-audio
 * 2. npx cap sync
 * 3. Ensure sound files are in public/sounds/
 */

import { Capacitor } from '@capacitor/core';

// NativeAudio will be loaded dynamically only on native platforms
let NativeAudio: any = null;

// ============================================================================
// SOUND CONFIGURATION
// ============================================================================

export interface SoundConfig {
  file: string;
  volume: number;
  preload?: boolean;
}

export const POKER_SOUNDS: { [key: string]: SoundConfig } = {
  // Action sounds
  'fold': { file: 'folding-37422.mp3', volume: 0.5, preload: true },
  'check': { file: 'button-click.mp3', volume: 0.5, preload: true },
  'call': { file: 'poker_chips1-87592.mp3', volume: 0.5, preload: true },
  'bet': { file: 'poker_chips1-87592.mp3', volume: 0.5, preload: true },
  'raise': { file: 'poker_chips1-87592.mp3', volume: 0.5, preload: true },
  'all-in': { file: 'allinpushchips-96121.mp3', volume: 0.7, preload: true },
  
  // Result sounds
  'win': { file: 'you-win-sequence-2-183949.mp3', volume: 0.6, preload: true },
  'lose': { file: 'marimba-lose-250960.mp3', volume: 0.25, preload: true },
  'jackpot': { file: 'jackpot.mp3', volume: 0.8, preload: true },
  
  // UI sounds
  'error': { file: 'marimba-lose-250960.mp3', volume: 0.4, preload: false },
  'notification': { file: 'button-click.mp3', volume: 0.3, preload: false },
  'turn': { file: 'button-click.mp3', volume: 0.4, preload: true },
};

// ============================================================================
// SOUND SERVICE CLASS
// ============================================================================

class SoundService {
  private isNative: boolean;
  private initialized: boolean = false;
  private enabled: boolean = true;
  private preloadedSounds: Set<string> = new Set();
  private audioCache: { [key: string]: HTMLAudioElement } = {};
  private audioUnlocked: boolean = false;
  private lastPlayedAt: { [key: string]: number } = {};

  constructor() {
    // Check if running on native platform - NativeAudio will be loaded later in initialize()
    this.isNative = Capacitor.isNativePlatform();
    console.log(`🔊 SoundService: Platform is ${this.isNative ? 'NATIVE' : 'WEB'}`);
  }

  /**
   * Initialize the sound service - call once on app start
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      if (this.isNative) {
        // Dynamically import NativeAudio only on native platforms
        try {
          const module = await import('@capacitor-community/native-audio');
          NativeAudio = module.NativeAudio;
          // Preload sounds for native
          await this.preloadNativeSounds();
        } catch (e) {
          // NativeAudio not installed - fall back to web audio
          console.log('🔊 NativeAudio not available, using web audio');
          this.isNative = false;
          this.setupWebAudioUnlock();
        }
      } else {
        // Setup web audio unlock
        this.setupWebAudioUnlock();
      }
      
      this.initialized = true;
      console.log('🔊 SoundService initialized');
    } catch (error) {
      console.error('🔊 SoundService initialization error:', error);
      // Fall back to web audio on any error
      this.isNative = false;
      this.setupWebAudioUnlock();
      this.initialized = true;
    }
  }

  /**
   * Preload sounds for Capacitor native audio
   */
  private async preloadNativeSounds(): Promise<void> {
    const preloadPromises: Promise<void>[] = [];

    for (const [key, config] of Object.entries(POKER_SOUNDS)) {
      if (config.preload) {
        preloadPromises.push(
          this.preloadNativeSound(key, config.file, config.volume)
        );
      }
    }

    await Promise.allSettled(preloadPromises);
    console.log(`🔊 Preloaded ${this.preloadedSounds.size} native sounds`);
  }

  /**
   * Preload a single native sound
   */
  private async preloadNativeSound(assetId: string, file: string, volume: number): Promise<void> {
    try {
      await NativeAudio.preload({
        assetId,
        assetPath: `public/sounds/${file}`,
        audioChannelNum: 1,
        volume,
        isUrl: false,
      });
      this.preloadedSounds.add(assetId);
    } catch (error) {
      // Sound might already be preloaded, or file not found
      console.warn(`🔊 Could not preload ${assetId}:`, error);
    }
  }

  /**
   * Setup web audio unlock for iOS Safari
   * iOS requires audio to be played during a user gesture to unlock
   */
  private setupWebAudioUnlock(): void {
    // Preload sounds immediately (they just won't play until unlocked)
    this.preloadWebSounds();
    
    const unlockHandler = async () => {
      if (this.audioUnlocked) return;

      console.log('🔊 Attempting to unlock web audio...');
      
      try {
        // Method 1: Try to play and immediately pause each cached sound
        // This "primes" them for iOS Safari
        const unlockPromises: Promise<void>[] = [];
        
        for (const [key, audio] of Object.entries(this.audioCache)) {
          const promise = (async () => {
            try {
              audio.muted = true;
              audio.volume = 0;
              await audio.play();
              audio.pause();
              audio.currentTime = 0;
              audio.muted = false;
              audio.volume = POKER_SOUNDS[key]?.volume || 0.5;
            } catch (e) {
              // Individual sound unlock failed, continue with others
            }
          })();
          unlockPromises.push(promise);
        }
        
        await Promise.allSettled(unlockPromises);
        
        // Method 2: Also create and unlock AudioContext if available
        if (typeof AudioContext !== 'undefined' || typeof (window as any).webkitAudioContext !== 'undefined') {
          const AudioCtx = AudioContext || (window as any).webkitAudioContext;
          const ctx = new AudioCtx();
          if (ctx.state === 'suspended') {
            await ctx.resume();
          }
          // Create a silent buffer and play it
          const buffer = ctx.createBuffer(1, 1, 22050);
          const source = ctx.createBufferSource();
          source.buffer = buffer;
          source.connect(ctx.destination);
          source.start(0);
        }
        
        this.audioUnlocked = true;
        console.log('🔊 Web audio unlocked successfully');
      } catch (error) {
        console.log('🔊 Audio unlock attempt - will retry on next interaction');
      }
    };

    // Listen for user interaction - keep listening until unlocked
    const persistentUnlockHandler = () => {
      unlockHandler();
      if (this.audioUnlocked) {
        window.removeEventListener('touchstart', persistentUnlockHandler);
        window.removeEventListener('touchend', persistentUnlockHandler);
        window.removeEventListener('click', persistentUnlockHandler);
        window.removeEventListener('keydown', persistentUnlockHandler);
      }
    };

    window.addEventListener('touchstart', persistentUnlockHandler, { passive: true });
    window.addEventListener('touchend', persistentUnlockHandler, { passive: true });
    window.addEventListener('click', persistentUnlockHandler, { passive: true });
    window.addEventListener('keydown', persistentUnlockHandler, { passive: true });
  }

  /**
   * Preload web sounds into cache
   * On iOS, these won't play until unlocked, but we load them early
   */
  private preloadWebSounds(): void {
    for (const [key, config] of Object.entries(POKER_SOUNDS)) {
      if (config.preload && !this.audioCache[key]) {
        try {
          const audio = new Audio(`/sounds/${config.file}`);
          audio.preload = 'auto';
          audio.volume = config.volume;
          // Set attributes that help with iOS
          audio.setAttribute('playsinline', 'true');
          audio.setAttribute('webkit-playsinline', 'true');
          audio.load();
          this.audioCache[key] = audio;
        } catch (e) {
          console.warn(`🔊 Could not preload ${key}:`, e);
        }
      }
    }
    console.log(`🔊 Preloaded ${Object.keys(this.audioCache).length} web sounds`);
  }

  /**
   * Play a sound by key
   */
  async play(soundKey: string): Promise<void> {
    if (!this.enabled) return;

    const config = POKER_SOUNDS[soundKey];
    if (!config) {
      console.warn(`🔊 Unknown sound: ${soundKey}`);
      return;
    }

    // Never stack a sound on itself. Two effects reacting to one state
    // change (the 7-2 bounty and the celebration both firing 'win', an
    // action echoed by two components) play it once. The long jingles get
    // a longer window so a second trigger cannot restart them mid-play.
    // Applies to the native (iOS/Android) path and the web path alike.
    const now = Date.now();
    const gap = soundKey === 'win' || soundKey === 'jackpot' ? 4000 : soundKey === 'lose' ? 2500 : 120;
    if (now - (this.lastPlayedAt[soundKey] || 0) < gap) return;
    this.lastPlayedAt[soundKey] = now;

    try {
      if (this.isNative) {
        await this.playNative(soundKey, config);
      } else {
        await this.playWeb(soundKey, config);
      }
    } catch (error) {
      console.error(`🔊 Error playing ${soundKey}:`, error);
    }
  }

  /**
   * Play sound using Capacitor NativeAudio
   */
  private async playNative(soundKey: string, config: SoundConfig): Promise<void> {
    try {
      if (this.preloadedSounds.has(soundKey)) {
        // Play preloaded sound
        await NativeAudio.play({ assetId: soundKey });
      } else {
        // Load and play on demand
        await NativeAudio.preload({
          assetId: soundKey,
          assetPath: `public/sounds/${config.file}`,
          audioChannelNum: 1,
          volume: config.volume,
          isUrl: false,
        });
        this.preloadedSounds.add(soundKey);
        await NativeAudio.play({ assetId: soundKey });
      }
    } catch (error) {
      console.error(`🔊 Native audio error for ${soundKey}:`, error);
    }
  }

  /**
   * Play sound using HTML5 Audio (web fallback)
   * iOS Safari requires using the same Audio element that was unlocked
   */
  private async playWeb(soundKey: string, config: SoundConfig): Promise<void> {
    try {
      let audio = this.audioCache[soundKey];
      
      if (!audio) {
        // Create and cache if not exists
        audio = new Audio(`/sounds/${config.file}`);
        audio.preload = 'auto';
        this.audioCache[soundKey] = audio;
      }
      
      // Reset to beginning and set volume
      audio.currentTime = 0;
      audio.volume = config.volume;
      
      // iOS Safari fix: if audio is already playing, we need to handle it
      if (!audio.paused) {
        // For overlapping sounds on iOS, we can't easily do this
        // Just let the current sound finish or restart it
        audio.currentTime = 0;
      }
      
      const playPromise = audio.play();
      if (playPromise) {
        await playPromise.catch((e) => {
          if (e.name === 'NotAllowedError') {
            console.log('🔊 Audio blocked - waiting for user interaction to unlock');
            // Audio will be unlocked on next user interaction
          } else if (e.name === 'AbortError') {
            // Play was interrupted, this is OK
          } else {
            console.warn(`🔊 Audio play error for ${soundKey}:`, e.message);
          }
        });
      }
    } catch (error) {
      // Silently fail for web audio
      console.warn(`🔊 Web audio error for ${soundKey}:`, error);
    }
  }

  /**
   * Play action sound (convenience method)
   */
  playAction(action: string): void {
    this.play(action);
  }

  /**
   * Play win sound
   */
  playWin(): void {
    this.play('win');
  }

  /**
   * Play lose sound
   */
  playLose(): void {
    this.play('lose');
  }

  /**
   * Play jackpot sound
   */
  playJackpot(): void {
    this.play('jackpot');
  }

  /**
   * Enable/disable sounds
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    console.log(`🔊 Sounds ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Check if sounds are enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Check if audio is unlocked (useful for iOS)
   */
  isUnlocked(): boolean {
    return this.audioUnlocked || this.isNative;
  }

  /**
   * Manually attempt to unlock audio - call this from a click/touch handler
   * Useful for iOS Safari where audio must be unlocked during user gesture
   */
  async tryUnlock(): Promise<boolean> {
    if (this.audioUnlocked || this.isNative) return true;
    
    try {
      // Try to play each cached sound muted
      for (const [key, audio] of Object.entries(this.audioCache)) {
        try {
          audio.muted = true;
          audio.volume = 0;
          await audio.play();
          audio.pause();
          audio.currentTime = 0;
          audio.muted = false;
          audio.volume = POKER_SOUNDS[key]?.volume || 0.5;
        } catch (e) {
          // Continue with others
        }
      }
      
      this.audioUnlocked = true;
      console.log('🔊 Audio manually unlocked');
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Check if running on native platform
   */
  isNativePlatform(): boolean {
    return this.isNative;
  }

  /**
   * Stop a specific sound (native only)
   */
  async stop(soundKey: string): Promise<void> {
    if (this.isNative && this.preloadedSounds.has(soundKey)) {
      try {
        await NativeAudio.stop({ assetId: soundKey });
      } catch (e) {
        // Ignore stop errors
      }
    }
  }

  /**
   * Cleanup - unload all sounds
   */
  async cleanup(): Promise<void> {
    if (this.isNative) {
      for (const assetId of this.preloadedSounds) {
        try {
          await NativeAudio.unload({ assetId });
        } catch (e) {
          // Ignore unload errors
        }
      }
      this.preloadedSounds.clear();
    }
    this.audioCache = {};
    this.initialized = false;
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

export const soundService = new SoundService();

// Auto-initialize when module loads
soundService.initialize();

export default soundService;